/**
 * Local first-contact estimate (Mifflin-St Jeor) for QuickHook.
 * Uses honest population defaults for height/weight until the full profile exists.
 */

import { pickPrimaryGoal } from './quickhook-goals.js';

const ACTIVITY_MULTIPLIER = {
  sedentary: 1.2,
  light: 1.375,
  moderate: 1.55,
  high: 1.725,
};

const DEFAULT_WEIGHT_KG = {
  female: 65,
  male: 78,
  other: 70,
};

const DEFAULT_HEIGHT_CM = {
  female: 162,
  male: 175,
  other: 168,
};

/**
 * @param {{ age: number, gender: string, activity: string, goal?: string, goals?: string[] }} input
 */
export function estimateQuickHookProfile(input) {
  const age = Number(input.age);
  const gender = String(input.gender || 'other');
  const activity = String(input.activity || 'sedentary');
  const goal = pickPrimaryGoal(input.goals || input.goal);

  const weightKg = DEFAULT_WEIGHT_KG[gender] ?? DEFAULT_WEIGHT_KG.other;
  const heightCm = DEFAULT_HEIGHT_CM[gender] ?? DEFAULT_HEIGHT_CM.other;

  const sexOffset = gender === 'male' ? 5 : gender === 'female' ? -161 : -78;
  const bmr = Math.round(10 * weightKg + 6.25 * heightCm - 5 * age + sexOffset);
  const multiplier = ACTIVITY_MULTIPLIER[activity] ?? ACTIVITY_MULTIPLIER.sedentary;
  const tdee = Math.round(bmr * multiplier);

  let calorieTarget = tdee;
  if (goal === 'lose_weight') calorieTarget = Math.round(tdee * 0.85);
  else if (goal === 'gain_muscle') calorieTarget = Math.round(tdee * 1.1);

  const calorieLow = Math.max(1200, calorieTarget - 100);
  const calorieHigh = calorieTarget + 100;

  return {
    weight_kg_assumed: weightKg,
    height_cm_assumed: heightCm,
    bmr,
    tdee,
    calorie_target: calorieTarget,
    calorie_low: calorieLow,
    calorie_high: calorieHigh,
    goal,
    goals: Array.isArray(input.goals) && input.goals.length ? input.goals : [goal],
    activity,
  };
}
